"use client";

import React, { useState, useEffect } from "react";
import {
  X,
  Check,
  ShieldCheck,
  Zap,
  Sparkles,
  ArrowRight,
  QrCode,
  CreditCard,
  Building2,
  FileText,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Plus,
  Clock,
  MessageSquare,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { PLANS, ADDONS, PlanTierKey, AddonKey } from "@/lib/pricingData";

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeSuccess?: () => void;
  currentTrialDays?: number;
  initialTier?: PlanTierKey | string;
}

export default function UpgradePlanModal({
  isOpen,
  onClose,
  onUpgradeSuccess,
  currentTrialDays = 14,
  initialTier = "growth",
}: UpgradePlanModalProps) {
  // Normalize initial tier
  const normalizedInitial: PlanTierKey =
    initialTier === "tier1"
      ? "starter"
      : initialTier === "tier2"
      ? "growth"
      : initialTier === "tier3"
      ? "professional"
      : (initialTier as PlanTierKey) || "growth";

  const [selectedTier, setSelectedTier] = useState<PlanTierKey>(normalizedInitial);
  const [selectedAddons, setSelectedAddons] = useState<AddonKey[]>([]);
  const [billingCountry, setBillingCountry] = useState("IN");
  const [buyerGstin, setBuyerGstin] = useState("");
  const [buyerStateCode, setBuyerStateCode] = useState("29"); // Default Karnataka
  const [paymentMode, setPaymentMode] = useState<"razorpay" | "upi" | "wire">("upi");

  const [agreedPaidConsent, setAgreedPaidConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [pendingVerificationData, setPendingVerificationData] = useState<any>(null);
  const [upiRefInput, setUpiRefInput] = useState("");
  const [copiedUpi, setCopiedUpi] = useState(false);

  const planKeys: PlanTierKey[] = ["starter", "growth", "professional", "business", "enterprise"];
  const currentPlan = PLANS[selectedTier] || PLANS.growth;
  const isIndia = billingCountry === "IN";
  const currencyKey: "INR" | "USD" | "AED" = isIndia ? "INR" : billingCountry === "AE" ? "AED" : "USD";
  const currencySymbol = isIndia ? "₹" : billingCountry === "AE" ? "AED " : "$";

  // Calculate base price + one-time setup fee + all selected add-ons
  const baseMonthly = currentPlan.pricing[currencyKey]?.monthly || 1999;
  const standardSetupFee = currentPlan.pricing[currencyKey]?.setup || 0;
  const setupFee = orderData?.setupFeeWaived ? 0 : standardSetupFee;
  const addonsTotal = selectedAddons.reduce((sum, key) => {
    const addon = ADDONS[key];
    if (!addon) return sum;
    const cost = addon.isRecurring
      ? (addon.pricing[currencyKey]?.monthly || 0)
      : (addon.pricing[currencyKey]?.setup || 0);
    return sum + cost;
  }, 0);
  const rawSubtotal = baseMonthly + setupFee + addonsTotal;

  const toggleAddon = (key: AddonKey) => {
    setSelectedAddons((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Modular add-on list with dynamic multi-currency pricing
  const availableAddons: { key: AddonKey; label: string; desc: string }[] = [
    {
      key: "autodialer_module",
      label: "Auto-Dialer Engine",
      desc: "Automated click-to-call campaign manager & speed-to-lead queue dispatcher",
    },
    {
      key: "onboarding_turnkey_vip",
      label: "VIP Turnkey Implementation",
      desc: "White-glove setup, Meta verification, telecaller onboarding rehearsal",
    },
    {
      key: "legacy_data_migration",
      label: "Legacy CRM & CSV Migration",
      desc: "Data cleansing, phone deduplication, and bulk ingestion of historic records",
    },
    {
      key: "payment_gateway_integration",
      label: "Payment Gateway Checkout",
      desc: "Automated payment link generation, receipt dispatch, and deal closing",
    },
    {
      key: "erp_tally_sync",
      label: "ERP & Accounting Webhook Sync",
      desc: "Bi-directional ledger & customer balance sync with Tally, Zoho, SAP",
    },
  ];

  // Create payment order whenever tier, addons, or country changes
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    async function initOrder() {
      try {
        const res = await fetch("/api/crm/subscription/create-order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planTier: selectedTier,
            billingCountry,
            buyerStateCode,
            buyerGstin,
            addons: selectedAddons,
          }),
        });

        if (res.ok && isMounted) {
          const data = await res.json();
          setOrderData(data);
        }
      } catch (e) {
        console.warn("[Init Order Error]:", e);
      }
    }

    initOrder();
    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedTier, billingCountry, buyerStateCode, buyerGstin, selectedAddons]);

  if (!isOpen) return null;

  const handleCopyUpi = () => {
    const upiId = orderData?.upiId || "9880893416@ybl";
    navigator.clipboard.writeText(upiId);
    setCopiedUpi(true);
    setTimeout(() => setCopiedUpi(false), 2000);
  };

  const handleCompleteUpgrade = async () => {
    if (!agreedPaidConsent) {
      setError("You must accept the Terms of Service, SLA, Refund Policy, and DPA to upgrade.");
      return;
    }

    // Guard against unconfigured automated gateway
    if (paymentMode === "razorpay" && !orderData?.isRazorpayConfigured) {
      setError("Online card gateway is pending activation. Please select Direct Business UPI or Wire / NetBanking.");
      return;
    }

    // Require transaction reference / UTR for manual payment methods
    if (paymentMode === "upi" || paymentMode === "wire") {
      const cleanRef = upiRefInput.trim();
      if (!cleanRef || cleanRef.length < 6) {
        setError(
          paymentMode === "upi"
            ? "Please enter your 12-digit UPI UTR Number (found in your Google Pay, PhonePe, or Paytm receipt)."
            : "Please enter your Bank IMPS / NEFT / RTGS Transaction Reference Number."
        );
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      // Record immutable legal consent audit log
      try {
        await fetch("/api/crm/consent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            consentType: "paid_checkout",
            documentIds: ["terms", "privacy", "sla", "refund", "dpa"],
          }),
        });
      } catch (consentErr) {
        console.warn("[Consent Log Non-fatal Error]:", consentErr);
      }

      const res = await fetch("/api/crm/subscription/verify-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: orderData?.orderId || `ORDER_${Date.now()}`,
          paymentId: upiRefInput.trim(),
          planTier: selectedTier,
          amount: orderData?.amount || rawSubtotal,
          currency: orderData?.currency || currencyKey,
          gateway: paymentMode,
          buyerGstin,
          buyerStateCode,
          billingCountry,
          upiRef: upiRefInput.trim(),
          addons: selectedAddons,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        if (data.pendingVerification) {
          // Display the Pending Verification state — NO instant free activation
          setPendingVerificationData(data);
        } else {
          setSuccess(true);
          if (onUpgradeSuccess) onUpgradeSuccess();
          setTimeout(() => {
            onClose();
            window.location.reload();
          }, 2200);
        }
      } else {
        setError(data.error || "Payment processing failed.");
      }
    } catch {
      setError("Failed to verify payment with server. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md overflow-y-auto">
      <div className="w-full max-w-4xl p-6 sm:p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl relative my-8 animate-in fade-in zoom-in-95 max-h-[92vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="text-center max-w-xl mx-auto mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
            <Zap className="w-3.5 h-3.5" />
            <span>Commercial Plan Upgrade & Entitlements</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
            Scale Your CRM <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-500">Pipeline & Seats</span>
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Zero per-seat SaaS tax. Transparent flat pricing with optional modular add-ons.
          </p>
        </div>

        {/* 1. Pending Verification State (Manual UPI / Wire receipt submitted) */}
        {pendingVerificationData ? (
          <div className="py-8 px-4 text-center space-y-5 animate-in fade-in zoom-in-95">
            <div className="w-16 h-16 bg-amber-500/10 text-amber-500 rounded-full flex items-center justify-center mx-auto ring-8 ring-amber-500/20">
              <Clock className="w-9 h-9" />
            </div>

            <div>
              <div className="inline-block px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                Payment Verification in Progress
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                Receipt Submitted for Verification
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 max-w-md mx-auto mt-1">
                Thank you! We have logged your upgrade request for{" "}
                <span className="font-bold text-primary">{currentPlan.name}</span>.
              </p>
            </div>

            {/* Receipt Details Table */}
            <div className="max-w-md mx-auto p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800 text-xs text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-400">Transaction Ref / UTR:</span>
                <span className="font-mono font-bold text-slate-900 dark:text-white">
                  {pendingVerificationData.referenceNumber}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Order / Request ID:</span>
                <span className="font-mono text-slate-700 dark:text-slate-300">
                  {pendingVerificationData.invoiceNumber || pendingVerificationData.orderId}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Plan Selected:</span>
                <span className="font-bold text-slate-900 dark:text-white">{currentPlan.name}</span>
              </div>
              {selectedAddons.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Add-Ons Included:</span>
                  <span className="text-slate-700 dark:text-slate-300 text-[11px]">
                    {selectedAddons.length} module{selectedAddons.length > 1 ? "s" : ""}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-800 font-bold">
                <span className="text-slate-900 dark:text-white">Total Amount:</span>
                <span className="text-primary text-sm">
                  {currencySymbol}
                  {(orderData?.amount || rawSubtotal).toLocaleString()}
                </span>
              </div>
            </div>

            {/* SLA Notice & WhatsApp Priority Button */}
            <div className="max-w-md mx-auto p-3.5 bg-blue-500/10 border border-blue-500/20 rounded-2xl text-[11px] text-blue-800 dark:text-blue-300 text-left space-y-2">
              <div className="flex items-center gap-2 font-bold">
                <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
                <span>Verification SLA: 1–2 Business Hours</span>
              </div>
              <p className="leading-relaxed">
                Our accounting team validates bank statement deposits within 1–2 hours during business days. For instant priority activation, please WhatsApp your payment screenshot to our billing desk.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <a
                href={`https://wa.me/919880893416?text=${encodeURIComponent(
                  `Hi Johnson Softwares, I have transferred payment for CRM Plan: ${currentPlan.name} (${currencySymbol}${(
                    orderData?.amount || rawSubtotal
                  ).toLocaleString()}). My UTR/Bank Reference is: ${
                    pendingVerificationData.referenceNumber
                  }. Please verify and activate.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20"
              >
                <MessageSquare className="w-4 h-4" />
                <span>WhatsApp Payment Screenshot</span>
              </a>

              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold text-xs"
              >
                Return to CRM Dashboard
              </button>
            </div>
          </div>
        ) : success ? (
          /* 2. Direct Activated State (Instant Payment) */
          <div className="text-center py-12 space-y-4 animate-in fade-in zoom-in-95">
            <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-2 ring-8 ring-emerald-500/20">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 dark:text-white">Upgrade Activated!</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 max-w-md mx-auto">
              Your company has been upgraded to the <span className="font-bold text-primary">{currentPlan.name}</span> plan.
              All seat limits and quotas are updated.
            </p>
            <div className="inline-block px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-xs font-mono text-slate-500">
              Applying changes & refreshing session...
            </div>
          </div>
        ) : (
          /* 3. Main Upgrade Form */
          <div className="space-y-6">
            {/* Tier Selection Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {planKeys.map((key) => {
                const plan = PLANS[key];
                const isSelected = selectedTier === key;
                const mPrice = plan.pricing[currencyKey]?.monthly || 1999;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedTier(key)}
                    className={`p-3.5 rounded-2xl border text-left transition-all relative ${
                      isSelected
                        ? "bg-primary/10 border-primary shadow-md ring-2 ring-primary/30"
                        : "bg-slate-50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-2.5 right-2 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-primary to-amber-500 text-white text-[9px] font-bold uppercase tracking-wider">
                        Popular
                      </span>
                    )}
                    <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">{plan.name}</h4>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{plan.seatsBadge}</p>
                    <div className="mt-2 text-sm font-extrabold text-slate-900 dark:text-white">
                      {currencySymbol}{mPrice.toLocaleString()}
                      <span className="text-[10px] font-normal text-slate-400">/mo</span>
                    </div>
                    {plan.pricing[currencyKey]?.setup > 0 && (
                      <div className="text-[9px] text-slate-500 dark:text-slate-400">
                        + {currencySymbol}{plan.pricing[currencyKey].setup.toLocaleString()} setup
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected Plan Details & Modular Add-Ons */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: Included Plan Features */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Included in {currentPlan.name}:
                  </h4>
                  <span className="text-[11px] font-bold text-primary">
                    {currentPlan.seatsBadge}
                  </span>
                </div>
                <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                  {currentPlan.displayFeatures.slice(0, 4).map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Selectable Modular Add-Ons with Real-time Calculation */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Optional Add-On Modules:
                  </h4>
                  {selectedAddons.length > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold">
                      +{currencySymbol}{addonsTotal.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="space-y-2 text-xs">
                  {availableAddons.map((item) => {
                    const addon = ADDONS[item.key];
                    if (!addon) return null;
                    const isChecked = selectedAddons.includes(item.key);

                    const priceBadge = addon.isRecurring
                      ? `+${currencySymbol}${(addon.pricing[currencyKey]?.monthly || 0).toLocaleString()}/mo`
                      : `+${currencySymbol}${(addon.pricing[currencyKey]?.setup || 0).toLocaleString()} one-time`;

                    return (
                      <label
                        key={item.key}
                        className={`flex items-start gap-2.5 p-2 rounded-xl cursor-pointer transition-colors border ${
                          isChecked
                            ? "bg-primary/5 border-primary/30 text-slate-900 dark:text-white"
                            : "border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleAddon(item.key)}
                          className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary h-4 w-4 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[11px] font-bold truncate">{item.label}</span>
                            <span className="text-[10px] font-mono font-semibold text-primary shrink-0">
                              {priceBadge}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 line-clamp-1 mt-0.5">
                            {item.desc}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Region & GST Details */}
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-primary" />
                  <span>Tax & Compliance Information</span>
                </h4>
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  <span className="text-slate-400">Region:</span>
                  <select
                    value={billingCountry}
                    onChange={(e) => setBillingCountry(e.target.value)}
                    className="px-2 py-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-xs"
                  >
                    <option value="IN">India (18% GST / MSME)</option>
                    <option value="AE">UAE / GCC (AED)</option>
                    <option value="US">Global / USA (USD)</option>
                  </select>
                </div>
              </div>

              {isIndia && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Buyer GSTIN (Optional for Tax Credit)
                    </label>
                    <input
                      type="text"
                      maxLength={15}
                      value={buyerGstin}
                      onChange={(e) => setBuyerGstin(e.target.value.toUpperCase())}
                      placeholder="29ABCDE1234F1Z5"
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs uppercase focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Buyer State
                    </label>
                    <select
                      value={buyerStateCode}
                      onChange={(e) => setBuyerStateCode(e.target.value)}
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs outline-none"
                    >
                      <option value="29">Karnataka (Intra-State: 9% CGST + 9% SGST)</option>
                      <option value="27">Maharashtra (Inter-State: 18% IGST)</option>
                      <option value="07">Delhi (Inter-State: 18% IGST)</option>
                      <option value="33">Tamil Nadu (Inter-State: 18% IGST)</option>
                      <option value="36">Telangana (Inter-State: 18% IGST)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Method Selector */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMode("upi")}
                  className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1 ${
                    paymentMode === "upi"
                      ? "bg-primary/10 border-primary shadow-sm"
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                  }`}
                >
                  <QrCode className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-bold text-slate-900 dark:text-white">Direct Business UPI</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMode("wire")}
                  className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1 ${
                    paymentMode === "wire"
                      ? "bg-primary/10 border-primary shadow-sm"
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                  }`}
                >
                  <Building2 className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-bold text-slate-900 dark:text-white">Wire / NetBanking</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMode("razorpay")}
                  className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center gap-1 ${
                    paymentMode === "razorpay"
                      ? "bg-primary/10 border-primary shadow-sm"
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                  }`}
                >
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span className="text-xs font-bold text-slate-900 dark:text-white">Razorpay / Card</span>
                </button>
              </div>

              {/* Payment Details Panel based on selected method */}
              {paymentMode === "upi" && (
                <div className="p-4 bg-slate-50 dark:bg-slate-950/60 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <QrCode className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-bold text-slate-900 dark:text-white">
                        Direct Business UPI Settlement
                      </span>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold">
                      Zero Surcharge
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                    <div>
                      <span className="text-[10px] text-slate-400 font-medium">Business UPI ID:</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-mono font-bold text-slate-900 dark:text-white">
                          {orderData?.upiId || "9880893416@ybl"}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopyUpi}
                          className="p-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700"
                          title="Copy UPI ID"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        {copiedUpi && <span className="text-[10px] text-emerald-600 font-bold">Copied!</span>}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Supported: Google Pay, PhonePe, Paytm, BHIM, CRED
                      </p>
                    </div>

                    <div>
                      <span className="text-[10px] text-slate-400 font-medium">Payee Name:</span>
                      <p className="font-bold text-slate-900 dark:text-white mt-0.5">Johnson Softwares</p>
                      <span className="text-[10px] text-slate-400 font-medium mt-1 block">Account Type:</span>
                      <p className="text-slate-700 dark:text-slate-300 font-semibold">Current Account (HDFC Bank)</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">
                      12-Digit UPI Transaction ID / UTR Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={upiRefInput}
                      onChange={(e) => setUpiRefInput(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
                      placeholder="e.g. 423819283741 (from your payment receipt)"
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Required for manual reconciliation. Our billing desk verifies bank receipt within 1–2 business hours.
                    </p>
                  </div>
                </div>
              )}

              {paymentMode === "wire" && (
                <div className="p-4 bg-slate-50 dark:bg-slate-950/60 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-bold text-slate-900 dark:text-white">
                        Bank Wire / NEFT / RTGS Transfer
                      </span>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold">
                      Commercial Account
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div>
                      <span className="text-[10px] text-slate-400">Account Name:</span>
                      <p className="font-bold text-slate-900 dark:text-white">Johnson Softwares</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400">Bank & Branch:</span>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">HDFC Bank Ltd, MG Road Bengaluru</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400">Account Number:</span>
                      <p className="font-mono font-bold text-primary">50200091823412</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400">IFSC / SWIFT:</span>
                      <p className="font-mono font-bold text-slate-800 dark:text-slate-200">HDFC0000240 / HDFCINBB</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 mb-1">
                      Bank Transaction Reference / UTR Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={upiRefInput}
                      onChange={(e) => setUpiRefInput(e.target.value)}
                      placeholder="e.g. CMS98234710294 or HDFCN26091129381"
                      className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-mono outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Our accounting team matches this reference against bank credits before activating the plan.
                    </p>
                  </div>
                </div>
              )}

              {paymentMode === "razorpay" && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-xs space-y-2 animate-in fade-in">
                  <div className="flex items-center gap-2 text-amber-800 dark:text-amber-400 font-bold">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <span>Online Card Gateway Pending Business Activation</span>
                  </div>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    Automated card checkout via Razorpay is currently undergoing business documentation setup.
                    Please select{" "}
                    <button
                      type="button"
                      onClick={() => setPaymentMode("upi")}
                      className="font-bold text-primary hover:underline"
                    >
                      Direct Business UPI
                    </button>{" "}
                    or{" "}
                    <button
                      type="button"
                      onClick={() => setPaymentMode("wire")}
                      className="font-bold text-blue-600 hover:underline"
                    >
                      Wire / NetBanking
                    </button>{" "}
                    above to settle your subscription directly with zero merchant fees.
                  </p>
                </div>
              )}
            </div>

            {/* Tax Breakdown & Statutory Notice */}
            <div className="p-3.5 bg-slate-100/70 dark:bg-slate-950/70 rounded-xl border border-slate-200 dark:border-slate-800 text-xs space-y-1.5">
              <div className="flex items-center justify-between font-medium text-slate-700 dark:text-slate-300">
                <span>{currentPlan.name} Monthly Subscription:</span>
                <span>{currencySymbol}{baseMonthly.toLocaleString()}</span>
              </div>
              {orderData?.setupFeeWaived ? (
                <div className="flex items-center justify-between font-medium text-emerald-600 dark:text-emerald-400">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    One-Time System Deployment &amp; Onboarding:
                  </span>
                  <span className="font-bold">Waived (Previously Paid) — {currencySymbol}0</span>
                </div>
              ) : setupFee > 0 ? (
                <div className="flex items-center justify-between font-medium text-slate-700 dark:text-slate-300">
                  <span>One-Time System Deployment &amp; Onboarding Setup:</span>
                  <span>{currencySymbol}{setupFee.toLocaleString()}</span>
                </div>
              ) : null}
              {selectedAddons.length > 0 && (
                <div className="flex items-center justify-between font-medium text-slate-700 dark:text-slate-300">
                  <span>Selected Add-On Modules ({selectedAddons.length}):</span>
                  <span>+{currencySymbol}{addonsTotal.toLocaleString()}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1 border-t border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 font-semibold">
                <span>Subtotal:</span>
                <span>{currencySymbol}{(orderData?.subtotal || rawSubtotal).toLocaleString()}</span>
              </div>
              {orderData?.invoiceType === "bill_of_supply" ? (
                <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 font-medium">
                  <span>Tax (Bill of Supply - MSME Threshold Exemption):</span>
                  <span>₹0.00 (0% GST)</span>
                </div>
              ) : (
                <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                  <span>Applicable GST ({orderData?.taxBreakdown?.taxRatePct || 18}%):</span>
                  <span>
                    +{currencySymbol}
                    {(orderData?.taxBreakdown?.totalTax || 0).toLocaleString()}
                  </span>
                </div>
              )}
              {orderData?.legalDeclarations?.[0] && (
                <p className="text-[10px] text-slate-500 dark:text-slate-400 italic pt-1 border-t border-slate-200/50 dark:border-slate-800">
                  {orderData.legalDeclarations[0]}
                </p>
              )}
            </div>

            {/* Click-Wrap Legal Agreements Checkbox */}
            <div className="p-3.5 bg-primary/5 rounded-2xl border border-primary/20 text-xs">
              <label className="flex items-start gap-2.5 cursor-pointer text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={agreedPaidConsent}
                  onChange={(e) => setAgreedPaidConsent(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary h-4 w-4 shrink-0"
                />
                <span className="text-[11px] leading-relaxed">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">
                    Terms of Service
                  </a>
                  ,{" "}
                  <a href="/sla" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">
                    99.9% Uptime SLA
                  </a>
                  ,{" "}
                  <a href="/refund" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">
                    Capacity Refund Policy
                  </a>
                  , and{" "}
                  <a href="/dpa" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">
                    Data Processing Addendum (DPA)
                  </a>
                  . I acknowledge that all cloud subscriptions represent provisioned infrastructure capacity and are strictly non-refundable.
                </span>
              </label>
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-xl text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Upgrade Button */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
              <div>
                <span className="text-xs text-slate-500">Total Investment:</span>
                <div className="text-2xl font-black text-slate-900 dark:text-white">
                  {currencySymbol}
                  {(orderData?.amount || rawSubtotal).toLocaleString()}
                </div>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-1/2 sm:w-auto px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <Button
                  onClick={handleCompleteUpgrade}
                  disabled={loading || !agreedPaidConsent || (paymentMode === "razorpay" && !orderData?.isRazorpayConfigured)}
                  className="w-1/2 sm:w-auto px-6 py-2.5 text-xs font-bold bg-primary hover:bg-primary/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? "Processing..."
                    : paymentMode === "razorpay" && !orderData?.isRazorpayConfigured
                    ? "Online Gateway Pending"
                    : `Submit Upgrade for ${currentPlan.name}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

